import { SourceType, Component, TransferRecord } from './interfaces';
import { PlayType } from '../../common/constants';
import { NetworkData } from '../../locality/networkDataStorage';

export function getComponent(sizeBytes: number): Component {
  return { id: 'payload', pos: 0, sizeBytes };
}

export class Record implements TransferRecord {
  type: SourceType;
  sourceId: string;
  nodeId: string;
  contentId: string;
  objectId: string;
  component: Component;
  timestamp: number;
  requestStart: number;
  loadInit: number;
  requestEnd: number;
  status: boolean;
  statusCode: number;
  tenantId: string;
  clusterId: string | undefined;
  externalIp: string | undefined;
  internalIp: string | undefined;
  internalMdns: string | undefined;
  appName: string;
  playType: string | undefined;
  peerEnabled: boolean;

  constructor(
    type: SourceType,
    nodeId: string,
    tenantId: string,
    sourceId: string | undefined | null,
    contentId: string,
    objectId: string,
    timestamp: number,
    requestStart: number,
    appName: string,
    playType: string | undefined,
    clusterId: string | undefined,
    peerEnabled: boolean
  ) {
    this.type = type;
    this.nodeId = nodeId;
    this.tenantId = tenantId;
    this.contentId = contentId;
    this.objectId = objectId;
    this.timestamp = Math.floor(timestamp);
    this.requestStart = Math.floor(requestStart);
    this.appName = appName;
    this.playType = playType ? playType : PlayType.UNKNOWN;
    this.clusterId = clusterId;

    // defaults are failed state defaults
    this.component = getComponent(0);
    this.statusCode = 500;
    // loadInit should be 0 here so that it can be seen as false, and be properly updated only once.
    this.loadInit = 0;
    // requestEnd should be greater than requestEnd so + 1 is good in this case
    this.requestEnd = this.requestStart + 1;
    this.status = false;
    this.sourceId = sourceId ? sourceId : 'undefined';
    this.peerEnabled = peerEnabled;
  }

  _setSourceId(sourceId: string | null | undefined): void {
    if (sourceId) {
      this.sourceId = sourceId;
    }
  }

  _checkLoadInit(): void {
    if (this.loadInit < this.requestStart) {
      this.loadInit = this.requestStart + 1;
    }
  }

  startLoading(loadInit: number): void {
    this.loadInit = Math.floor(loadInit);
  }

  fail(
    requestEnd: number,
    sourceId: string | null | undefined,
    statusCode: number
  ): void {
    this.status = false;
    this._checkLoadInit();
    this._setSourceId(sourceId);
    this.statusCode = statusCode;
    this.requestEnd = Math.floor(requestEnd);
  }

  setNetworkData(net: NetworkData) {
    if (!net) {
      return;
    }
    const { externalIp, internalIp, internalMdns } = net;
    this.externalIp = externalIp;
    this.internalIp = internalIp;
    this.internalMdns = internalMdns;
  }

  stop(
    requestEnd: number,
    sourceId: string | null | undefined,
    totalBytes: number,
    statusCode?: number
  ): void {
    this._checkLoadInit();
    this.requestEnd = Math.floor(requestEnd);
    this._setSourceId(sourceId);
    this.component = getComponent(totalBytes);
    this.statusCode = statusCode ? statusCode : 200;
    this.status = true;
  }
}

export function PeerRecord(
  nodeId: string,
  tenantId: string,
  sourceId: string,
  contentId: string,
  objectId: string,
  requestStart: number,
  appName: string,
  playType: string,
  clusterId: string | undefined
): Record {
  return new Record(
    SourceType.PEER,
    nodeId,
    tenantId,
    sourceId,
    contentId,
    objectId,
    Date.now(),
    requestStart,
    appName,
    playType,
    clusterId,
    true
  );
}

export function IngestRecord(
  nodeId: string,
  tenantId: string,
  sourceId: string,
  contentId: string,
  objectId: string,
  requestStart: number,
  appName: string,
  playType: string,
  clusterId: string | undefined,
  peerEnabled: boolean
): Record {
  return new Record(
    SourceType.INGEST,
    nodeId,
    tenantId,
    sourceId,
    contentId,
    objectId,
    Date.now(),
    requestStart,
    appName,
    playType,
    clusterId,
    peerEnabled
  );
}
